import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'localization';
import type { ApiClients } from '../../../context/ApiClientsContext';
import type { Sphere } from 'types/global';
import type {
  ScopeMode,
  SphereSubscriptionWithChannel,
  EffectiveSubscription,
  Channel,
} from 'types/channel';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import './SphereChannelsTab.scss';

export interface SphereChannelsTabProps {
  sphere: Sphere;
  organizationId: string;
  apiClients: ApiClients | null;
  hasEditPermission: boolean;
}

const SphereChannelsTab: React.FC<SphereChannelsTabProps> = ({
  sphere,
  organizationId,
  apiClients,
  hasEditPermission,
}) => {
  const { t } = useTranslation();

  const [effectiveSubscriptions, setEffectiveSubscriptions] = useState<EffectiveSubscription[]>([]);
  const [directSubscriptions, setDirectSubscriptions] = useState<SphereSubscriptionWithChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMutating, setIsMutating] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  // Add channel dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [channelSearchTerm, setChannelSearchTerm] = useState('');
  const [availableChannels, setAvailableChannels] = useState<Channel[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [scopeMode, setScopeMode] = useState<ScopeMode>('direct');
  const [submitting, setSubmitting] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const refetch = useCallback(() => {
    setFetchTrigger((prev) => prev + 1);
  }, []);

  // Fetch subscriptions
  useEffect(() => {
    if (!apiClients?.schemaClient || !sphere.id) {
      setEffectiveSubscriptions([]);
      setDirectSubscriptions([]);
      return;
    }

    let cancelled = false;

    const fetchSubscriptions = async () => {
      setLoading(true);
      setError(null);

      try {
        const [effectiveRes, directRes] = await Promise.all([
          apiClients.schemaClient.get<{ data: EffectiveSubscription[] }>(
            '/channels/sphere-subscriptions/effective',
            { params: { sphereId: sphere.id } }
          ),
          apiClients.schemaClient.get<{ data: SphereSubscriptionWithChannel[]; total: number }>(
            '/channels/sphere-subscriptions',
            { params: { sphereId: sphere.id } }
          ),
        ]);

        if (!cancelled) {
          setEffectiveSubscriptions(effectiveRes.data);
          setDirectSubscriptions(directRes.data);
        }
      } catch {
        if (!cancelled) {
          setError(t('sphereChannels.errors.loadFailed'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSubscriptions();

    return () => {
      cancelled = true;
    };
  }, [apiClients, sphere.id, fetchTrigger, t]);

  const handleUnsubscribe = useCallback(
    async (subscriptionId: string) => {
      if (!apiClients?.schemaClient) return;

      setIsMutating(true);
      try {
        await apiClients.schemaClient.delete(
          `/channels/sphere-subscriptions/${encodeURIComponent(subscriptionId)}`,
          { params: { sphereId: sphere.id } }
        );
        refetch();
      } catch {
        setError(t('sphereChannels.errors.unsubscribeFailed'));
      } finally {
        setIsMutating(false);
      }
    },
    [apiClients, sphere.id, refetch, t]
  );

  const handleScopeChange = useCallback(
    async (subscriptionId: string, newScope: ScopeMode) => {
      if (!apiClients?.schemaClient) return;

      setIsMutating(true);
      try {
        await apiClients.schemaClient.patch(
          `/channels/sphere-subscriptions/${encodeURIComponent(subscriptionId)}`,
          { scopeMode: newScope },
          { params: { sphereId: sphere.id } }
        );
        refetch();
      } catch {
        setError(t('sphereChannels.errors.updateFailed'));
      } finally {
        setIsMutating(false);
      }
    },
    [apiClients, sphere.id, refetch, t]
  );

  // Add channel dialog handlers
  const openAddDialog = useCallback(async () => {
    if (!apiClients?.schemaClient) return;

    setShowAddDialog(true);
    setChannelSearchTerm('');
    setSelectedChannelId(null);
    setScopeMode('direct');
    setDialogError(null);
    setSubmitting(false);
    setChannelsLoading(true);

    try {
      const result = await apiClients.schemaClient.get<{ channels: Channel[] }>(
        '/channels/list',
        { params: { status: 'approved', limit: 50 } }
      );
      setAvailableChannels(result.channels);
    } catch {
      setAvailableChannels([]);
    } finally {
      setChannelsLoading(false);
    }
  }, [apiClients, organizationId]);

  const closeAddDialog = useCallback(() => {
    if (submitting) return;
    setShowAddDialog(false);
  }, [submitting]);

  const handleAddOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) closeAddDialog();
    },
    [closeAddDialog]
  );

  const handleAddKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') closeAddDialog();
    },
    [closeAddDialog]
  );

  const handleSubscribe = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!apiClients?.schemaClient || !selectedChannelId) return;

      setSubmitting(true);
      setDialogError(null);

      try {
        await apiClients.schemaClient.post('/channels/sphere-subscriptions', {
          channelId: selectedChannelId,
          sphereId: sphere.id,
          scopeMode,
        });
        setShowAddDialog(false);
        refetch();
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 409 || (err instanceof Error && err.message.includes('409'))) {
          setDialogError(t('sphereChannels.addChannel.errorDuplicate'));
        } else {
          setDialogError(t('sphereChannels.addChannel.error'));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [apiClients, selectedChannelId, sphere.id, scopeMode, organizationId, refetch, t]
  );

  const filteredChannels = availableChannels.filter((ch) => {
    if (!channelSearchTerm.trim()) return true;
    const name = getLocalizedText(ch.name).toLowerCase();
    const desc = getLocalizedText(ch.description).toLowerCase();
    const term = channelSearchTerm.toLowerCase();
    return name.includes(term) || desc.includes(term);
  });

  const inheritedSubscriptions = effectiveSubscriptions.filter((s) => s.source === 'inherited');

  if (loading) {
    return (
      <div className="sphere-channels-tab">
        <div className="sphere-channels-tab__loading">
          <i className="pi pi-spin pi-spinner" />
          <span>{t('sphereChannels.loading')}</span>
        </div>
      </div>
    );
  }

  if (error && directSubscriptions.length === 0 && effectiveSubscriptions.length === 0) {
    return (
      <div className="sphere-channels-tab">
        <div className="sphere-channels-tab__error">{error}</div>
      </div>
    );
  }

  return (
    <div className="sphere-channels-tab">
      {hasEditPermission && (
        <div className="sphere-channels-tab__toolbar">
          <button
            type="button"
            className="sphere-channels-tab__add-btn"
            onClick={openAddDialog}
            disabled={isMutating}
          >
            <i className="pi pi-plus" />
            <span>{t('sphereChannels.addChannel.button')}</span>
          </button>
        </div>
      )}

      {error && (
        <div className="sphere-channels-tab__error">{error}</div>
      )}

      {/* Direct Subscriptions */}
      <div className="sphere-channels-tab__section">
        <h4 className="sphere-channels-tab__section-title">
          {t('sphereChannels.list.directTitle')}
        </h4>
        {directSubscriptions.length === 0 ? (
          <p className="sphere-channels-tab__empty">
            {t('sphereChannels.list.emptyDirect')}
          </p>
        ) : (
          <div className="sphere-channels-tab__list">
            {directSubscriptions.map((sub) => (
              <div key={sub.id} className="sphere-channels-tab__card">
                <div className="sphere-channels-tab__card-info">
                  <div className="sphere-channels-tab__card-header">
                    <i className="pi pi-megaphone" />
                    <span className="sphere-channels-tab__card-name">
                      {getLocalizedText(sub.channel.name)}
                    </span>
                    <span className="sphere-channels-tab__badge sphere-channels-tab__badge--direct">
                      {t('sphereChannels.card.directBadge')}
                    </span>
                  </div>
                  {sub.channel.description && getLocalizedText(sub.channel.description) && (
                    <p className="sphere-channels-tab__card-desc">
                      {getLocalizedText(sub.channel.description)}
                    </p>
                  )}
                </div>
                {hasEditPermission && (
                  <div className="sphere-channels-tab__card-actions">
                    <button
                      type="button"
                      className={`sphere-channels-tab__scope-toggle ${
                        sub.scope_mode === 'recursive'
                          ? 'sphere-channels-tab__scope-toggle--recursive'
                          : ''
                      }`}
                      onClick={() =>
                        handleScopeChange(
                          sub.id,
                          sub.scope_mode === 'direct' ? 'recursive' : 'direct'
                        )
                      }
                      title={t('sphereChannels.scopeMode.label')}
                      disabled={isMutating}
                    >
                      <i className="pi pi-sitemap" />
                      <span>
                        {sub.scope_mode === 'recursive'
                          ? t('sphereChannels.scopeMode.recursive')
                          : t('sphereChannels.scopeMode.direct')}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="sphere-channels-tab__unsubscribe-btn"
                      onClick={() => handleUnsubscribe(sub.id)}
                      disabled={isMutating}
                    >
                      <i className="pi pi-times" />
                      <span>{t('sphereChannels.card.unsubscribe')}</span>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Inherited Subscriptions */}
      <div className="sphere-channels-tab__section">
        <h4 className="sphere-channels-tab__section-title">
          {t('sphereChannels.list.inheritedTitle')}
        </h4>
        {inheritedSubscriptions.length === 0 ? (
          <p className="sphere-channels-tab__empty">
            {t('sphereChannels.list.emptyInherited')}
          </p>
        ) : (
          <div className="sphere-channels-tab__list">
            {inheritedSubscriptions.map((sub) => (
              <div key={`${sub.channelSystemName}-${sub.sourceSphereId}`} className="sphere-channels-tab__card">
                <div className="sphere-channels-tab__card-info">
                  <div className="sphere-channels-tab__card-header">
                    <i className="pi pi-megaphone" />
                    <span className="sphere-channels-tab__card-name">
                      {getLocalizedText(sub.channel.name)}
                    </span>
                    <span className="sphere-channels-tab__badge sphere-channels-tab__badge--inherited">
                      {t('sphereChannels.card.inheritedBadge', {
                        sphereName: sub.sourceSphereName,
                      })}
                    </span>
                  </div>
                  {sub.channel.description && getLocalizedText(sub.channel.description) && (
                    <p className="sphere-channels-tab__card-desc">
                      {getLocalizedText(sub.channel.description)}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Channel Dialog */}
      {showAddDialog && (
        <div
          className="marketplace-dialog-overlay"
          onClick={handleAddOverlayClick}
          onKeyDown={handleAddKeyDown}
        >
          <div
            className="marketplace-dialog sphere-channels-tab__add-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sphere-channels-add-dialog-title"
          >
            <div className="marketplace-dialog__header">
              <h2 id="sphere-channels-add-dialog-title" className="marketplace-dialog__title">
                {t('sphereChannels.addChannel.title')}
              </h2>
              <button
                type="button"
                className="marketplace-dialog__close-btn"
                onClick={closeAddDialog}
                aria-label={t('sphereChannels.addChannel.cancel')}
              >
                &times;
              </button>
            </div>

            <form className="marketplace-dialog__body" onSubmit={handleSubscribe}>
              <div className="sphere-channels-tab__search">
                <i className="pi pi-search sphere-channels-tab__search-icon" />
                <input
                  type="text"
                  className="marketplace-dialog__input sphere-channels-tab__search-input"
                  placeholder={t('sphereChannels.addChannel.searchPlaceholder')}
                  value={channelSearchTerm}
                  onChange={(e) => setChannelSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="sphere-channels-tab__channel-list">
                {channelsLoading && (
                  <div className="sphere-channels-tab__dialog-loading">
                    <i className="pi pi-spin pi-spinner" />
                  </div>
                )}

                {!channelsLoading && filteredChannels.length === 0 && (
                  <div className="sphere-channels-tab__dialog-empty">
                    {t('sphereChannels.addChannel.noResults')}
                  </div>
                )}

                {!channelsLoading &&
                  filteredChannels.map((ch) => (
                    <button
                      key={ch.id}
                      type="button"
                      className={`sphere-channels-tab__channel-option ${
                        selectedChannelId === ch.id
                          ? 'sphere-channels-tab__channel-option--selected'
                          : ''
                      }`}
                      onClick={() => setSelectedChannelId(ch.id)}
                    >
                      <i className="pi pi-megaphone" />
                      <div className="sphere-channels-tab__channel-option-info">
                        <span className="sphere-channels-tab__channel-option-name">
                          {getLocalizedText(ch.name)}
                        </span>
                        {ch.description && getLocalizedText(ch.description) && (
                          <span className="sphere-channels-tab__channel-option-desc">
                            {getLocalizedText(ch.description)}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
              </div>

              <div className="sphere-channels-tab__scope-section">
                <label className="marketplace-dialog__label">
                  {t('sphereChannels.scopeMode.label')}
                </label>
                <div className="sphere-channels-tab__scope-options">
                  <button
                    type="button"
                    className={`sphere-channels-tab__scope-opt ${
                      scopeMode === 'direct' ? 'sphere-channels-tab__scope-opt--selected' : ''
                    }`}
                    onClick={() => setScopeMode('direct')}
                  >
                    <i className="pi pi-circle" />
                    <span>{t('sphereChannels.scopeMode.direct')}</span>
                  </button>
                  <button
                    type="button"
                    className={`sphere-channels-tab__scope-opt ${
                      scopeMode === 'recursive' ? 'sphere-channels-tab__scope-opt--selected' : ''
                    }`}
                    onClick={() => setScopeMode('recursive')}
                  >
                    <i className="pi pi-sitemap" />
                    <span>{t('sphereChannels.scopeMode.recursive')}</span>
                  </button>
                </div>
              </div>

              {dialogError && (
                <div className="marketplace-dialog__error">{dialogError}</div>
              )}

              <div className="marketplace-dialog__actions">
                <button
                  type="button"
                  className="marketplace-dialog__cancel-btn"
                  onClick={closeAddDialog}
                  disabled={submitting}
                >
                  {t('sphereChannels.addChannel.cancel')}
                </button>
                <button
                  type="submit"
                  className="marketplace-dialog__submit-btn"
                  disabled={!selectedChannelId || submitting}
                >
                  {submitting
                    ? t('sphereChannels.addChannel.subscribing')
                    : t('sphereChannels.addChannel.confirm')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SphereChannelsTab;
